import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormControlStatus, FormGroup } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipGrid, MatChipInputEvent } from '@angular/material/chips';
import { ChipColorEnum } from '@app/core/enums/chip-color.enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { ValidatorType } from '@app/core/enums/validator-type.enum';
import { AbstractFieldBuilder } from '@app/shared/components/fields/abstract.field';
import { getValue, simplifyStringForSearch } from '@app/shared/extra/utils';
import { Observable } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';


@Component({
  selector: 'chips-field-builder',
  templateUrl: './chips-field-builder.component.html',
  styleUrls: ['./chips-field-builder.component.scss'],
  standalone: false
})
export class ChipsFieldBuilderComponent extends AbstractFieldBuilder<FormControl<string>> implements OnInit, AfterViewInit {
  public Permission = PermissionEnum;
  public chips: any[] = [];
  public filteredOptions: Observable<string[]>;
  public readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  @ViewChild('inputField') public inputField: ElementRef<HTMLInputElement>;
  @ViewChild('suggestions') public suggestions: MatAutocomplete;
  @ViewChild('chipList') public chipList: MatChipGrid;

  public ngOnInit(): void {
    this.chips.push(...this.fieldInitValue);
    this.form = new FormGroup({
      field: new FormControl('', this.computeValidators({values: this.chips}))
    });

    // Propagate invalid status to the mat-chip-list
    this.form.controls.field.statusChanges.subscribe(
      (status: FormControlStatus) => {
        if (this.chipList !== void 0) {
          this.chipList.errorState = status === 'INVALID';
        }
      });

    // Initialise the field in the registry
    this.setFieldValue(this.fieldInitValue);
    this.setFieldInitialValue(this.fieldInitValue);
    this.getNextState();

    this.filteredOptions = this.form.controls.field.valueChanges.pipe(
      startWith(''),
      filter(value => value !== void 0),
      map(value => this.filter(value))
    );
  }

  public ngAfterViewInit(): void {
    this.setupHooks();
  }

  protected calcFieldInitValue(): void {
    this.fieldInitValue =
      (!this.isCreatingEntity && this.fieldConfig) ? (getValue(this.entity, this.fieldConfig.fieldPath) ?? []) : [];
  }

  /**
   * Get the chip background-color if it's a colored chip.
   * The color depends on the chip's position.
   * @param index  the chip's position
   * @return background-color or undefined if there is no color.
   */
  public getChipColor(index: number): { 'background-color': string } {
    if (!this.fieldConfig.customOptions.colored) return void 0;
    const colors = Object.values(ChipColorEnum);
    return {'background-color': colors[index % colors.length]};
  }

  public remove(chip: any): void {
    const index = this.chips.indexOf(chip);

    if (index >= 0) {
      this.chips.splice(index, 1);
    }

    this.setFieldValue(this.chips.slice());
  }

  public input(event: MatChipInputEvent): void {
    const value = event.value;
    const maxlength = this.fieldConfig.field?.validators.find(validator => validator.type === ValidatorType.MAX_LENGTH)
        ?.definition
      || this.appConfig.DEFAULT_MAX_LENGTH;
    if (value.length <= maxlength && this.form.valid) {
      event.chipInput.clear();
      this.add(value);
    }
  }

  public select(event: MatAutocompleteSelectedEvent): void {
    const value = event.option.value;
    this.inputField.nativeElement.value = '';
    this.add(value);
  }

  public cancel(): void {
    this.chips = (this.getFieldInitialValue() || []).slice();
    this.form.controls.field.setValue('');
    this.setFieldValue((this.getFieldInitialValue() || []).slice());
    this.getNextState();
  }

  private add(value: any): void {
    // Add the new chip
    if ((value || '').trim()) {
      this.chips.push(value.trim());
    }

    this.form.controls.field.setValue('');
    this.setFieldValue(this.chips.slice());
  }

  private filter(value: string): string[] {
    return this.fieldConfig.field?.fieldValues
      .filter(option => simplifyStringForSearch(option).includes(simplifyStringForSearch(value))
        && !this.chips.includes(option));
  }

  /**
   * Set focus on the element
   */
  public focus(): void {
    if (this.focusable) {
      setTimeout(() => this.inputField.nativeElement.focus());
    }
  }
}
